import { DownOutlined, InfoCircleOutlined, GroupOutlined } from '@ant-design/icons';
import { smallTimeOptions } from 'assets/styles/constants';
import SuperFetch from 'library/helpers/superFetch';
import WithHyperLink from 'library/hoc/withHyperLink';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { Notification } from 'zComponents/atoms';
import RenderSiteName from 'zComponents/atoms/RenderSiteName';
import { AssetGroupActionsDropdown } from 'zComponents/molecules';
import { Button, Card, Divider, Dropdown, Flex, Modal, Row, Space, Tabs, Typography } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';
import EditAssetGroupModal from '../ManageAssetGroupModal';
import RenderAssetsForAssetGroup from './components/RenderAssets';
import RenderGroupPermissions from './components/RenderGroupPermissions';
import GroupTitleWrapper from './styles';

const { Text, Paragraph } = Typography;

const DEFAULT_TAB = 'Assets';
const ViewAssetGroupModal = ({ children, data }) => {
	// Hooks
	const dispatch = useDispatch();

	// HOC
	const SiteNameWithLink = WithHyperLink(RenderSiteName);

	// basic information for this modal to be used in the form.
	let { groupID } = data;

	// Global Data
	const { data: assetGroupsData } = useSelector((state) => state.AssetGroups);
	const {
		remoteConfig: { constants }
	} = useSelector((state) => state.FbRemoteConfig);

	// Local States
	const [allGroups, setAllGroups] = useState([]);
	const [group, setGroup] = useState({});
	const [manageModalVisible, setManageModalVisible] = useState(false);
	const [tabKey, setTabKey] = useState(DEFAULT_TAB);
	const [visible, setVisible] = useState(false);

	// get Groups data from the store. (this is the data that is used in the modal)
	useEffect(() => {
		if (assetGroupsData.length > 0 && visible) {
			setAllGroups(assetGroupsData);
			setGroup(assetGroupsData.find((g) => g.groupID === groupID) || {});
		}
	}, [assetGroupsData, groupID, visible]);

	const handleCancel = () => {
		setVisible(false);
	};

	const onEditName = (value) => {
		if (group.name === value) {
			return;
		} else {
			if (/^[a-zA-Z0-9 ]+$/.test(value)) {
				const oldGroup = group;
				setGroup({ ...group, name: value });
				SuperFetch.patch('/deviceGroups/' + group.groupID, { name: value })
					.then((res) => {
						if (res) {
							if (res.statusCode === 200) {
								dispatch(assetGroupActions.loadGroups());
								Notification('success', 'Group Name Updated.');
							} else {
								setGroup(oldGroup);
								Notification('error', 'Update Failed, Please try again.');
							}
						}
						return res;
					})
					.catch((err) => {
						Notification('error', 'Update Failed, Please try again.');
					});
			} else {
				Notification('error', 'Asset Group Name can only contain letters, numbers and spaces.');
			}
		}
	};
	const renderGroups = () =>
		allGroups.map((group, key) => {
			return {
				key: key,
				label: <p onClick={() => setGroup(group)}>{group.name}</p>,
				icon: <GroupOutlined />
			};
		});

	const tabs = [
		{
			label: 'Assets',
			key: 'Assets',
			children: <RenderAssetsForAssetGroup group={group} />
		},
		{
			label: 'Permissions',
			key: 'Permissions',
			children: <RenderGroupPermissions groupID={groupID} />
		}
	];

	const actions = {
		Assets: (
			<EditAssetGroupModal data={group} visible={manageModalVisible} setVisible={setManageModalVisible}>
				<Button size={'small'} onClick={() => setManageModalVisible(true)}>
					Manage
				</Button>
			</EditAssetGroupModal>
		)
	};

	const isPermittedUpdateGroup = useManagePermittedRoles('updateGroup', {
		orgID: [group.orgID],
		siteID: [group.siteID],
		floorID: []
	});

	return (
		<Fragment>
			<ConditionalTooltipButton
				buttonText={'View'}
				trueTitle={''}
				falseTitle={''}
				onClick={() => setVisible(true)}
			>
				{children}
			</ConditionalTooltipButton>

			<Modal
				title={
					<Row justify='space-between'>
						<div style={{ width: '70%' }}>
							<GroupTitleWrapper
								editable={
									isPermittedUpdateGroup
										? {
												tooltip: 'click to edit Group name',
												maxLength: 100,
												autoSize: { maxRows: 2 },
												icon: <InfoCircleOutlined />,
												triggerType: ['text', 'icon'],
												onChange: (value) => onEditName(value)
										  }
										: false
								}
							>
								{group.name || 'Group Not Found'}
							</GroupTitleWrapper>
						</div>
						{allGroups?.length > 0 ? (
							<Dropdown
								menu={{
									items: renderGroups()
								}}
								trigger={['click']}
							>
								<Button
									icon={<DownOutlined />}
									style={{ display: 'flex', alignItems: 'center', flexDirection: 'row-reverse' }}
								>
									Change Group
								</Button>
							</Dropdown>
						) : null}
					</Row>
				}
				open={visible}
				onCancel={handleCancel}
				destroyOnClose={true}
				width={1200}
				footer={null}
				closable={false}
			>
				<Card>
					<Flex justify='space-between' align='center'>
						<Text
							style={{
								fontSize: '16px',
								color: 'black',
								textOverflow: 'ellipsis',
								overflow: 'hidden',
								whiteSpace: 'nowrap',
								wordBreak: 'break-all',
								display: 'inline-block'
							}}
						>
							About Group
						</Text>
						<div>
							<AssetGroupActionsDropdown group={group} />
						</div>
					</Flex>

					{group.description && (
						<div className='descContainer'>
							<Paragraph className='description'>{group.description}</Paragraph>
						</div>
					)}
					<Space split={<Divider type='vertical' />}>
						<div>
							<label className='infoLabel'>Type</label>
							<p className='infoProp'>{group.typeName}</p>
						</div>
						<div>
							<label className='infoLabel'>Site</label>
							<SiteNameWithLink dataID={group.siteID} type='site' />
						</div>
						<div>
							<label className='infoLabel'>Assets</label>
							<p className='infoProp'>{group?.assets?.length}</p>
						</div>
					</Space>
					<Row className='metaContainer'>
						<Space wrap className='dateContainer'>
							<p
								className='metaField'
								title={new Date(group.modifiedDate).toLocaleString('en-AU', smallTimeOptions)}
							>
								<span className='metaLabel'>Last updated: </span>
								{new Date(group.modifiedDate).toLocaleString('en-AU', smallTimeOptions)}
							</p>
							<p
								className='metaField'
								title={new Date(group.createdDate).toLocaleString('en-AU', smallTimeOptions)}
							>
								<span className='metaLabel'>Created on: </span>
								{new Date(group.createdDate).toLocaleString('en-AU', smallTimeOptions)}
							</p>
						</Space>
					</Row>
				</Card>
				<Divider style={{ margin: '10px 0px' }} />
				{group.typeID === constants.ACCESS_CONTROL ? (
					<Tabs
						items={tabs}
						size='small'
						defaultActiveKey={'Assets'}
						type='card'
						animated={{ inkBar: true, tabPane: true }}
						tabBarExtraContent={actions[tabKey]}
						onChange={(activeKey) => setTabKey(activeKey)}
					/>
				) : (
					<RenderAssetsForAssetGroup group={group} showHeader />
				)}
			</Modal>
		</Fragment>
	);
};

export default ViewAssetGroupModal;
